import React, { useState, useEffect } from 'react';
import { useSound } from '../../contexts/SoundContext';
import styles from './BootScreen.module.css';

interface BootScreenProps {
  onBootComplete: () => void;
}

export const BootScreen: React.FC<BootScreenProps> = ({ onBootComplete }) => {
  const [phase, setPhase] = useState<'loading' | 'welcome' | 'done'>('loading');
  const { playSound, enableSounds } = useSound();

  useEffect(() => {
    // Enable sounds on any click during boot
    const handleClick = () => {
      enableSounds();
    };
    document.addEventListener('click', handleClick);
    return () => document.removeEventListener('click', handleClick);
  }, [enableSounds]);

  useEffect(() => {
    // Fast boot: ~1.5 seconds loading, then welcome screen
    const bootTimer = setTimeout(() => {
      setPhase('welcome');
      playSound('startup');
    }, 1500);

    return () => clearTimeout(bootTimer);
  }, [playSound]);

  useEffect(() => {
    if (phase === 'welcome') {
      // Show welcome briefly, then transition to login
      const welcomeTimer = setTimeout(() => {
        setPhase('done');
        onBootComplete();
      }, 1500);
      return () => clearTimeout(welcomeTimer);
    }
  }, [phase, onBootComplete]);

  if (phase === 'done') return null;

  return (
    <div
      className={`${styles.bootScreen} ${phase === 'welcome' ? styles.fadeOut : ''}`}
      role="status"
      aria-label="Loading Windows XP"
    >
      {phase === 'loading' && (
        <div className={styles.loadingContainer}>
          {/* Windows Logo */}
          <div className={styles.logo}>
            <img
              src="/icons/windows-logo.png"
              alt="Windows"
              className={styles.logoImage}
            />
            <div className={styles.logoText}>
              <span className={styles.microsoft}>Microsoft</span>
              <span className={styles.windowsXp}>
                Windows<span className={styles.xp}>XP</span>
              </span>
            </div>
          </div>

          {/* Progress Bar - 3 sliding blocks */}
          <div className={styles.progressContainer}>
            <div className={styles.progressBar}>
              <div className={styles.progressTrack}>
                <div className={styles.progressBlock} />
                <div className={styles.progressBlock} />
                <div className={styles.progressBlock} />
              </div>
            </div>
          </div>
        </div>
      )}

      {phase === 'welcome' && (
        <div className={styles.welcomeContainer}>
          <div className={styles.welcomeText}>Welcome</div>
        </div>
      )}

      {/* Microsoft Corporation text */}
      <div className={styles.copyright}>
        Microsoft Corporation
      </div>

      {/* Click anywhere prompt */}
      <div className={styles.clickPrompt}>
        Click anywhere to enable sounds
      </div>
    </div>
  );
};

export default BootScreen;
